import { Fragment } from 'react';

import type { CopyRunsProps } from './CopyRuns.types';
import type { CopySegment } from '@personal-trainer-app/shared/src/utils/text';

/** The one tone a shared sentence carries without being asked: start-options' missed count. */
function warningTone(segment: CopySegment): string | undefined {
  return 'tone' in segment && segment.tone === 'warning'
    ? 'text-warning-400'
    : undefined;
}

/**
 * CopyRuns - a sentence composed in shared, set inside the caller's own line.
 *
 * Shared presenters return sentences as `CopySegment` runs rather than markup:
 * the words a sentence turns on are `strong`, its numbers are `figure`s, and
 * each platform decides how those look. This is that decision for web, in one
 * place, so a component never hand-rolls the loop. Mobile's twin is
 * `primitives/CopyRuns`.
 *
 * Plain runs stay bare text, so they inherit the line's colour, size and face.
 *
 * @example
 * <p className="text-body-sm text-neutral-400">
 *   <CopyRuns segments={line} />
 * </p>
 *
 * // A band's colour on its figures
 * <CopyRuns segments={verdict} runClassName={(s) => TONE_CLASSES[s.tone]} />
 */
export function CopyRuns<T extends CopySegment>({
  segments,
  strongClassName = 'font-semibold text-neutral-50',
  figureClassName = 'font-mono font-semibold tabular-nums text-neutral-300',
  runClassName = warningTone,
}: CopyRunsProps<T>): React.JSX.Element {
  return (
    <>
      {segments.map((segment, index) => {
        // Segments are positional runs of one composed sentence, so their
        // index is their identity: there is no id to key on.
        const key = `${index}-${segment.text}`;
        const own = runClassName(segment);

        if (segment.figure) {
          return (
            <span
              key={key}
              className={own ? `${figureClassName} ${own}` : figureClassName}
            >
              {segment.text}
            </span>
          );
        }
        if (segment.strong) {
          return (
            <strong
              key={key}
              className={own ? `${strongClassName} ${own}` : strongClassName}
            >
              {segment.text}
            </strong>
          );
        }
        if (own) {
          return (
            <span key={key} className={own}>
              {segment.text}
            </span>
          );
        }
        return <Fragment key={key}>{segment.text}</Fragment>;
      })}
    </>
  );
}

export default CopyRuns;
